import { useState, useCallback, useRef, useEffect } from 'react';
import log from '../utilities/log';
import mediator from '../utilities/mediator';
import i18n from '../utilities/i18n';
import { DOWNLOAD_CONFIG, DOWNLOAD_MODE, DownloadStatus } from '../constants';
import { type DownloadPackageResult } from '../services/study';
import {
  downloadFileFromBlob,
  downloadFileFromStream,
  supportsFileDownload,
} from '../utilities/downloadFile';

export interface ExportParams {
  downloadMode: (typeof DOWNLOAD_MODE)[keyof typeof DOWNLOAD_MODE];
  studyKey: string;
  studyName: string;
  subjectKeys?: string[];
}

export interface DownloadParams {
  packageUrl: string;
  checksumUrl: string | undefined;
  packageName: string;
  checksumName: string | undefined;
  signal?: AbortSignal;
}

export interface DownloadState {
  status: DownloadStatus;
  progress?: number;
  jobKey?: string;
  rawDownloadUrl?: string;
  downloadBlobUrl?: string;
  filename?: string;
  checksumDownloadUrl?: string;
  checksumFilename?: string;
  errorMessage?: string;
}

export interface StudyDownloadResult {
  exportStudyData: (params: ExportParams) => Promise<void>;
  downloadFiles: (params: DownloadParams) => Promise<void>;
  resetDownload: () => void;
  isPending: boolean;
  downloadState: DownloadState;
}

/**
 * Hook for exporting study data with progress tracking and download handling
 */
export function useStudyDownload(): StudyDownloadResult {
  const [downloadState, setDownloadState] = useState<DownloadState>({
    status: DownloadStatus.IDLE,
  });
  const activeRequest = useRef(false);
  const latestRequestId = useRef(0);
  const pollingController = useRef<AbortController | undefined>(undefined);

  useEffect(
    () => () => {
      latestRequestId.current += 1;
      pollingController.current?.abort();
    },
    [],
  );

  const exportStudyData = useCallback(
    // Request refs, rather than captured state, keep this callback stable.
    async (params: ExportParams) => {
      // Prevent concurrent downloads
      if (activeRequest.current) {
        const error = new Error(
          i18n.t('Modal_Download.error_already_in_progress'),
        );

        log.warn('Download already in progress, rejecting new request');
        throw error;
      }

      activeRequest.current = true;
      const requestId = ++latestRequestId.current;
      const controller = new AbortController();

      pollingController.current = controller;

      try {
        // Set initial status
        setDownloadState({
          status: DownloadStatus.PREPARING,
          progress: 0,
        });

        // Set processing status
        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.PROCESSING,
        }));

        // Build the mediator request
        let topic: string;
        let requestData: Record<string, any>;

        switch (params.downloadMode) {
          case DOWNLOAD_MODE.STUDY:
            topic = 'study:downloadStudyData';
            requestData = { studyKey: params.studyKey };
            break;
          case DOWNLOAD_MODE.SUBJECTS:
            topic = 'study:downloadSelectedSubjectData';
            requestData = { subjectKeys: params.subjectKeys };
            break;
          default:
            throw new Error(`Invalid download mode: ${params.downloadMode}`);
        }

        const mediatorRequest = {
          ...requestData,
          signal: controller.signal,
          onJobCreated: (jobKey: string) => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({ ...prev, jobKey }));
          },
          onAcquiring: () => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({
              ...prev,
              status: DownloadStatus.ACQUIRING,
              progress: 100,
            }));
          },
          onProgress: (progressPercent: number) => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({
              ...prev,
              progress: progressPercent,
            }));
          },
          onReconnecting: (reconnecting: boolean) => {
            if (requestId !== latestRequestId.current) {
              return;
            }

            setDownloadState((prev) => ({
              ...prev,
              status: reconnecting
                ? DownloadStatus.RECONNECTING
                : DownloadStatus.PROCESSING,
            }));
          },
        };

        // Use the service for actual download
        const downloadResult = await mediator.request<DownloadPackageResult>(
          topic,
          mediatorRequest,
        );

        if (requestId !== latestRequestId.current) {
          return;
        }

        const { downloadUrl: url, checksumSha256 } = downloadResult;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `${params.studyName}_${timestamp}${DOWNLOAD_CONFIG.FILE_EXTENSION}`;

        let checksumDownloadUrl: string | undefined;
        let checksumFilename: string | undefined;

        if (checksumSha256) {
          const checksumText = `${checksumSha256}  ${filename}\n`;
          const checksumBlob = new Blob([checksumText], {
            type: 'text/plain;charset=utf-8',
          });

          checksumDownloadUrl = window.URL.createObjectURL(checksumBlob);
          checksumFilename = `${filename}.sha256`;
        }

        // Clean up download URLs and update state
        setDownloadState((prev) => {
          if (
            prev.checksumDownloadUrl &&
            prev.checksumDownloadUrl !== checksumDownloadUrl
          ) {
            window.URL.revokeObjectURL(prev.checksumDownloadUrl);
          }

          return {
            ...prev,
            status: DownloadStatus.COMPLETED,
            rawDownloadUrl: url,
            filename,
            checksumDownloadUrl,
            checksumFilename,
            errorMessage: undefined,
          } as DownloadState;
        });
      } catch (error) {
        if (requestId !== latestRequestId.current) {
          return;
        }

        log.error('download failed:', error);

        setDownloadState((prev) => ({
          ...prev,
          status: DownloadStatus.FAILED,
          errorMessage:
            error instanceof Error
              ? error.message
              : i18n.t('Modal_Download.error_generic_failed'),
        }));
        throw error;
      } finally {
        if (requestId === latestRequestId.current) {
          activeRequest.current = false;
        }
      }
    },
    [],
  );

  const downloadFiles = useCallback(
    async ({
      packageUrl,
      checksumUrl,
      packageName,
      checksumName,
      signal,
    }: DownloadParams) => {
      const result = await downloadFileFromStream(
        packageUrl,
        packageName,
        signal,
      );

      if (signal?.aborted) {
        throw new DOMException('Download cancelled', 'AbortError');
      }

      if (!supportsFileDownload()) {
        // Need to attempt blob download of package
        if (!(result instanceof Blob)) {
          throw new Error('Download failed');
        }

        const blobUrl = URL.createObjectURL(result);

        downloadFileFromBlob(blobUrl, packageName);

        setDownloadState((prev) => {
          if (prev.downloadBlobUrl && prev.downloadBlobUrl !== blobUrl) {
            window.URL.revokeObjectURL(prev.downloadBlobUrl);
          }

          return {
            ...prev,
            downloadBlobUrl: blobUrl,
            filename: packageName,
          };
        });
      }

      if (checksumUrl && checksumName) {
        downloadFileFromBlob(checksumUrl, checksumName);
      }
    },
    [],
  );

  // Clean up download URLs and reset state
  const resetDownload = useCallback(() => {
    // Stop browser polling only; closing this modal does not cancel generation.
    latestRequestId.current += 1;
    pollingController.current?.abort();
    activeRequest.current = false;

    if (downloadState.checksumDownloadUrl) {
      window.URL.revokeObjectURL(downloadState.checksumDownloadUrl);
    }

    if (downloadState.downloadBlobUrl) {
      window.URL.revokeObjectURL(downloadState.downloadBlobUrl);
    }

    setDownloadState({
      status: DownloadStatus.IDLE,
    });
  }, [downloadState.checksumDownloadUrl, downloadState.downloadBlobUrl]);

  return {
    exportStudyData,
    downloadFiles,
    resetDownload,
    isPending:
      downloadState.status === DownloadStatus.PREPARING ||
      downloadState.status === DownloadStatus.PROCESSING ||
      downloadState.status === DownloadStatus.ACQUIRING ||
      downloadState.status === DownloadStatus.RECONNECTING,
    downloadState,
  };
}

export default useStudyDownload;
